/**
 * ErrorBoundary.tsx
 *
 * Reusable React class-component error boundary.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 *   // Custom fallback:
 *   <ErrorBoundary fallback={<p>Something broke here.</p>}>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 *   // With reset callback (e.g. clear state before retry):
 *   <ErrorBoundary onReset={() => clearSomeState()} resetLabel="Retry">
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 * Notes:
 *  - Error boundaries are React class components by requirement.
 *  - They catch render errors + errors thrown during lifecycle methods and
 *    constructors of child components.
 *  - They do NOT catch async errors (fetch, setTimeout) or event handlers —
 *    those need try/catch at the call site.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children:   ReactNode;
  /** Custom fallback UI — if omitted a sensible default is rendered. */
  fallback?:  ReactNode;
  /** Called when the user clicks the reset button (before re-mounting children). */
  onReset?:   () => void;
  /** Label for the reset button. Defaults to "Try again". */
  resetLabel?: string;
}

interface State {
  hasError:  boolean;
  error:     Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', info.componentStack);
  }

  handleReset = (): void => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Custom fallback — caller controls the entire UI
    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }

    // Default fallback
    const { resetLabel = 'Try again' } = this.props;
    return (
      <div style={styles.container}>
        <div style={styles.icon}>⚠</div>
        <div style={styles.message}>Something went wrong.</div>
        {this.state.error?.message && (
          <div style={styles.detail}>{this.state.error.message}</div>
        )}
        <button style={styles.button} onClick={this.handleReset}>
          {resetLabel}
        </button>
      </div>
    );
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        '40px 24px',
    textAlign:      'center',
    fontFamily:     "'DM Sans', sans-serif",
    color:          '#888',
  },
  icon: {
    fontSize:     '28px',
    marginBottom: '12px',
    color:        '#ca5f5f',
  },
  message: {
    fontSize:     '16px',
    fontWeight:   600,
    marginBottom: '6px',
    color:        '#ccc',
  },
  detail: {
    fontSize:     '12px',
    color:        '#666',
    marginBottom: '16px',
    maxWidth:     '400px',
    wordBreak:    'break-word',
  },
  button: {
    padding:      '8px 20px',
    borderRadius: '8px',
    border:       '1px solid #444',
    background:   'transparent',
    color:        '#ccc',
    cursor:       'pointer',
    fontSize:     '13px',
  },
};

export default ErrorBoundary;
